import React from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/controls/Modal";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import { ChevronRight, GuardrailsModalIllustration, Workflow } from "@dreamhub/designsystem/icons";
import { BlockedWorkflow } from "@/pages/contracts/hooks";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  blockedBy: BlockedWorkflow[];
  onSelectWorkflow: (workflowId: string) => void;
}

const OnboardingGuardrailsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  blockedBy,
  onSelectWorkflow
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={isOpen}
      setIsOpen={onClose}
      headerClassname="border-none"
      contentClassname="w-[640px]"
      data-test="onboarding-guardrails-modal"
      title={
        <div className="flex flex-col items-center gap-4 pt-4">
          <GuardrailsModalIllustration />
          <Text as="div" variant="lgSubtitleH1" className="text-center">
            {t("guardrails.modal.title")}
          </Text>
        </div>
      }
      content={
        <div className="flex flex-col gap-6 px-6 pb-4">
          <Text as="div" variant="body" className="text-center">
            {blockedBy.length === 1
              ? t("guardrails.modal.workflowScoreMessage", {
                  questionnaireName: blockedBy[0].workflowName ?? ""
                })
              : t("guardrails.modal.multipleWorkflowsMessage", {
                  count: blockedBy.length
                })}
          </Text>
          <div className="flex flex-col gap-3 max-h-[40vh] overflow-y-auto">
            {blockedBy.map((workflow) => (
              <button
                key={workflow.workflowId}
                type="button"
                onClick={() => onSelectWorkflow(workflow.workflowId)}
                data-test="onboarding-guardrails-modal-workflow-row"
                className="flex items-center gap-8 p-3 rounded-lg bg-surface-elevation2 border border-border-border1 text-left transition-colors hover:bg-surface-elevation3"
              >
                <div className="flex items-center gap-2">
                  <IconContainer size="xl" color="primary">
                    <Workflow />
                  </IconContainer>
                  <Text
                    variant="body"
                    className="truncate max-w-[230px]"
                    hasTruncatedTooltip
                  >
                    {workflow.workflowName}
                  </Text>
                </div>
                <Text variant="bodySecondary">
                  {t("guardrails.modal.requiredMissing")}
                </Text>
                <IconContainer size="5" color="secondary" className="ml-auto">
                  <ChevronRight />
                </IconContainer>
              </button>
            ))}
          </div>
          <Text as="div" variant="body" className="text-center">
            {t("guardrails.modal.updateMessage")}
          </Text>
        </div>
      }
      footer={
        <div className="flex justify-center gap-2 mt-4">
          <Button
            data-test="onboarding-guardrails-modal-cancel-button"
            variant="secondary"
            onClick={onClose}
          >
            {t("cancel")}
          </Button>
          <Button
            data-test="onboarding-guardrails-modal-update-button"
            onClick={() => onSelectWorkflow(blockedBy[0].workflowId)}
            disabled={blockedBy.length === 0}
          >
            {t("guardrails.modal.updateWorkflows")}
          </Button>
        </div>
      }
      hideDivider={true}
    />
  );
};

export default OnboardingGuardrailsModal;
